import { type FC } from "react";
import { Grid, GridItem, useBreakpointValue } from "@chakra-ui/react";
import BarChartIcon from "assets/icons/medium/bar-chart.svg?react";
import FlameIcon from "assets/icons/medium/flame.svg?react";
import { SkeletonGrid } from "components/skeleton-grid";
import { StatsCard } from "components/stats-card";
import { StatsCardSkeleton } from "components/stats-card-skeleton";

export type StatDefinition = {
  id: string;
  label: string;
  value: number | string;
  icon: FC;
  iconBgColor: string;
  iconColor: string;
};

export type StatsGridProps = {
  stats: StatDefinition[];
  isLoading: boolean;
};

export function StatsGrid({ stats, isLoading }: StatsGridProps) {
  const isMobile = useBreakpointValue({ base: true, sm: false });

  const columns = isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)";
  const rows = isMobile ? "repeat(3, 1fr)" : "repeat(2, 1fr)";

  return (
    <SkeletonGrid
      isLoaded={!isLoading}
      gridSize={6}
      skeleton={<StatsCardSkeleton />}
      templateColumns={columns}
      templateRows={rows}
    >
      <Grid templateColumns={columns} templateRows={rows} gap={6}>
        {stats.map((stat) => (
          <GridItem key={stat.id} w={"100%"}>
            <StatsCard
              stat={stat}
              icon={stat.icon}
              iconBgColor={stat.iconBgColor}
              iconColor={stat.iconColor}
            />
          </GridItem>
        ))}
      </Grid>
    </SkeletonGrid>
  );
}

// Helper function to create common stats from event data
export function createOpenHomeStats({
  attendeeCount,
  repeatAttendees,
  avgPriceIndication,
  hotAttendees,
  warmAttendees,
  coldAttendees,
  showInterestLevel = true,
}: {
  attendeeCount: number;
  repeatAttendees: number;
  avgPriceIndication: string;
  hotAttendees: number;
  warmAttendees: number;
  coldAttendees: number;
  showInterestLevel?: boolean;
}): StatDefinition[] {
  return [
    {
      id: "attendees",
      label: "Attendees",
      value: attendeeCount,
      icon: BarChartIcon,
      iconBgColor: "indigo.100",
      iconColor: "gray.700",
    },
    {
      id: "multiple-inspections",
      label: "Total repeat attendees",
      value: repeatAttendees,
      icon: BarChartIcon,
      iconBgColor: "indigo.100",
      iconColor: "gray.700",
    },
    {
      id: "avg-price-indication",
      label: "Average price indicator",
      value: avgPriceIndication,
      icon: BarChartIcon,
      iconBgColor: "indigo.100",
      iconColor: "gray.700",
    },
    ...(showInterestLevel
      ? [
          {
            id: "hot-attendees",
            label: "Hot attendees",
            value: hotAttendees,
            icon: FlameIcon,
            iconBgColor: "red.100",
            iconColor: "red.500",
          },
          {
            id: "warm-attendees",
            label: "Warm attendees",
            value: warmAttendees,
            icon: FlameIcon,
            iconBgColor: "orange.100",
            iconColor: "orange.500",
          },
          {
            id: "cold-attendees",
            label: "Cold attendees",
            value: coldAttendees,
            icon: FlameIcon,
            iconBgColor: "blue.50",
            iconColor: "blue.500",
          },
        ]
      : []),
  ];
}
